/**
 * GoalWidget — Goal & Budget Tracking widget (Story 5.30 → Epic 37 redesign)
 *
 * Displays a visual progress bar toward a target value with pacing
 * indicators and status-based coloring. Supports budget, metric, and
 * custom goal types.
 *
 * Shares the chart-widget shell (quiet uppercase title, size-aware padding,
 * compact preview frame) so it renders as one family with the rest of the
 * redesigned widgets. Semantic status colors (on-track / warning / danger /
 * achieved / exceeded) are MEANINGFUL and kept as-is; the shell only owns the
 * neutral chrome around them.
 */
import type { ReactNode } from "react";
import { Flag, TrendingUp, AlertTriangle, AlertCircle, CheckCircle, Calendar } from "lucide-react";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { WidgetDataEnvelope, AnyWidgetPayload, GoalStatus } from "../../../lib/widgetData";
import { isGoalData } from "../../../lib/widgetData";
import { formatGoalValue } from "../../../lib/goalFormat";
import { useChartWidgetShell, ChartMessage } from "./chartWidgetShell";

// ── Status color mapping ────────────────────────────────────────────

const STATUS_COLORS: Record<GoalStatus, { bar: string; text: string; label: string }> = {
  "on-track": { bar: "bg-blue-600", text: "text-blue-600", label: "On Track" },
  warning: { bar: "bg-amber-500", text: "text-amber-500", label: "Warning" },
  danger: { bar: "bg-red-500", text: "text-red-500", label: "Danger" },
  achieved: { bar: "bg-green-500", text: "text-green-500", label: "Achieved" },
  exceeded: { bar: "bg-red-500", text: "text-red-500", label: "Over Budget" },
};

const STATUS_ICONS: Record<GoalStatus, typeof TrendingUp> = {
  "on-track": TrendingUp,
  warning: AlertTriangle,
  danger: AlertCircle,
  achieved: CheckCircle,
  exceeded: AlertCircle,
};

// ── Value formatting ────────────────────────────────────────────────
// Extracted to lib/goalFormat.ts (Story 18.3 Task 3) — the single formatting
// source shared with the Goals page (18.8) and the client Goals card (18.3),
// so the report widget and the app surfaces never disagree on a value.

// ── Props ───────────────────────────────────────────────────────────

interface GoalWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<AnyWidgetPayload>;
  isPreviewMode?: boolean;
}

// ── Component ───────────────────────────────────────────────────────

export function GoalWidget({ widget, data, isPreviewMode }: GoalWidgetProps) {
  const config = widget.config;
  const goalType = (config.goalType as string) ?? "";
  const targetValue = (config.targetValue as number) ?? 0;
  const title = (config.title as string) ?? widget.title;
  const { compact, shell } = useChartWidgetShell(widget.config);

  const shellBody = (body: ReactNode, opts?: { centered?: boolean }) =>
    shell("goal", title, body, opts);

  // ── Edit mode ───────────────────────────────────────────────────
  // Story 18.4 — a bound widget (savedGoalId/savedBudgetId) shows "Linked goal"
  // / "Linked budget" instead of "Configure goal": the saved record is the
  // source of truth, so there is nothing to configure here. The live populate
  // round-trip (preview mode) renders the real progress bar.
  const savedGoalId = (config.savedGoalId as string) ?? "";
  const savedBudgetId = (config.savedBudgetId as string) ?? "";
  const linkedLabel = savedGoalId
    ? "Linked goal"
    : savedBudgetId
      ? "Linked budget"
      : null;
  if (!isPreviewMode) {
    return shellBody(
      <div className="flex flex-col items-center justify-center gap-2 text-center">
        <Flag className="h-8 w-8 text-muted-foreground/50" />
        {linkedLabel ? (
          <span className="text-xs text-muted-foreground">{linkedLabel}</span>
        ) : !goalType ? (
          <span className="text-xs text-muted-foreground">Configure goal</span>
        ) : (
          <span className="text-xs capitalize text-muted-foreground">{goalType} goal</span>
        )}
      </div>,
      { centered: true },
    );
  }

  // ── No target configured ────────────────────────────────────────
  if (!targetValue || !goalType) {
    return shellBody(
      <div className="flex flex-col items-center justify-center gap-2 text-center">
        <Flag className="h-8 w-8 text-muted-foreground/50" />
        <span className="text-sm text-muted-foreground">Configure a goal</span>
      </div>,
      { centered: true },
    );
  }

  // ── Loading state ───────────────────────────────────────────────
  if (data?.loading) {
    return shellBody(
      <div className="flex w-full flex-col gap-3">
        <div className="h-3 w-full animate-pulse rounded-full bg-muted" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
      </div>,
    );
  }

  // ── Error state ─────────────────────────────────────────────────
  if (data?.error) {
    return shellBody(<ChartMessage role="alert">{data.error}</ChartMessage>, { centered: true });
  }

  // ── No data / integration not connected ─────────────────────────
  if (!data?.data || !isGoalData(data.data)) {
    // Manual current value — show 0% state
    const manualCurrentValue = (config.currentValue as number) ?? 0;
    if (manualCurrentValue === 0 && goalType === "metric") {
      return shellBody(
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <Flag className="h-6 w-6 text-muted-foreground/50" />
          <span className="text-xs text-muted-foreground">
            Connect an integration to track progress automatically
          </span>
        </div>,
        { centered: true },
      );
    }

    return shellBody(
      <div className="flex w-full flex-col gap-3">
        <ProgressBar percentage={0} status="on-track" />
        <span className="text-xs text-muted-foreground">Awaiting data</span>
      </div>,
    );
  }

  // ── Render goal data ────────────────────────────────────────────
  const goalData = data.data;
  const statusConfig = STATUS_COLORS[goalData.status];
  const StatusIcon = STATUS_ICONS[goalData.status];
  const showPacing = (config.showPacing as boolean) ?? true;
  const showPercentage = (config.showPercentage as boolean) ?? true;
  const showDaysRemaining = (config.showDaysRemaining as boolean) ?? true;

  const currentFormatted = formatGoalValue(goalData.currentValue, goalData.unit, goalData.currencyCode);
  const targetFormatted = formatGoalValue(goalData.targetValue, goalData.unit, goalData.currencyCode);

  // Budget labels
  const percentLabel = goalData.goalType === "budget"
    ? `${goalData.percentage.toFixed(0)}% of budget`
    : `${goalData.percentage.toFixed(0)}% complete`;

  const ariaLabel = `${goalData.goalType === "budget" ? "Budget" : "Goal"}: ${currentFormatted} of ${targetFormatted} ${goalData.unit === "currency" ? "spent" : ""}, ${percentLabel}, ${statusConfig.label}`;

  return shellBody(
    <div className="flex w-full flex-col gap-2" aria-label={ariaLabel} data-testid="goal-content">
      {/* Value-first: the current/target value leads */}
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-lg font-semibold text-foreground" style={{ letterSpacing: "-0.01em" }}>
          {currentFormatted} / {targetFormatted}
        </span>
        {showPercentage && (
          <span
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[12px] font-semibold leading-none ${statusConfig.text}`}
            style={{ backgroundColor: "color-mix(in srgb, currentColor 12%, transparent)" }}
          >
            <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {percentLabel}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <ProgressBar percentage={goalData.percentage} status={goalData.status} />

      {/* Pacing indicator */}
      {showPacing && !compact && goalData.pacing && (
        <div className="text-xs text-muted-foreground">
          {goalData.pacing.isOnTrack ? (
            <span>
              On track to reach {formatGoalValue(goalData.pacing.projectedValue, goalData.unit, goalData.currencyCode)} by end of period
            </span>
          ) : (
            <span className="text-amber-600">
              Projected: {formatGoalValue(goalData.pacing.projectedValue, goalData.unit, goalData.currencyCode)} of {targetFormatted} — below target
            </span>
          )}
        </div>
      )}

      {/* Days remaining */}
      {showDaysRemaining && !compact && goalData.pacing && goalData.pacing.daysRemaining > 0 && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground/80">
          <Calendar className="h-3 w-3" aria-hidden="true" />
          <span>{goalData.pacing.daysRemaining} days remaining</span>
        </div>
      )}

      {/* Period ended badge */}
      {goalData.pacing && goalData.pacing.daysRemaining <= 0 && (
        <div className="flex items-center gap-1">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            goalData.status === "achieved"
              ? "bg-green-100 text-green-700"
              : goalData.status === "exceeded"
                ? "bg-red-100 text-red-700"
                : "bg-muted text-muted-foreground"
          }`}>
            Period ended — {statusConfig.label}
          </span>
        </div>
      )}
    </div>,
  );
}

// ── Progress Bar subcomponent ────────────────────────────────────────

function ProgressBar({ percentage, status }: { percentage: number; status: GoalStatus }) {
  const fillWidth = Math.min(Math.max(percentage, 0), 100);
  const statusConfig = STATUS_COLORS[status];

  return (
    <div className="relative h-2 w-full rounded-full bg-muted" aria-hidden="true">
      <div
        className={`h-full rounded-full transition-all duration-500 ${statusConfig.bar}`}
        style={{ width: `${fillWidth}%` }}
      />
      {/* Target marker at 100% */}
      <div className="absolute right-0 top-0 h-full w-0.5 bg-muted-foreground/40" />
    </div>
  );
}
